import { useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { mapFullAddressFromCaseDetailsAddress } from 'api';
import { EditIcon } from 'components/v2/icons/EditIcon';
import { InputField } from 'components/v2/input/InputField';
import Modal from 'components/v2/modal/Modal';
import { MODALS_STORE } from 'constants/ui/modal';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { useLocalization } from 'localization';
import { editAddressSchema } from 'components/v2/division/form/schema/createOrderSchema';
import { NumberInputField } from 'components/v2/input/NumberInputField';

export type AddressFormValues = {
  fullAddress?: string;
  street: string;
  houseNumber: string;
  postNumber: string;
  city: string;
  area: string;
  appartmentNumber?: string;
  floor?: string;
  longitude?: number;
  latitude?: number;
};

type Props = {
  onAddAddress: (address?: Partial<AddressFormValues>) => void;
  modalId: string;
  error?: FieldError;
  defaultValue?: AddressFormValues;
};

const CreateOrderKeyPickUpAddress = (props: Props) => {
  const { onAddAddress, modalId, error, defaultValue } = props;
  const { t } = useLocalization();
  const { toggleModal, isModalOpen } = MODALS_STORE.createOrder();
  const [editAddressKey, setEditAddressKey] = useState(1);

  const { control, setValue, watch, trigger, getValues, handleSubmit } = useForm({
    resolver: yupResolver(editAddressSchema()),
    defaultValues: defaultValue,
    values: defaultValue,
  });

  const handleSaveAddress = async () => {
    const isValid = await trigger();
    if (isValid) {
      const addressFull = mapFullAddressFromCaseDetailsAddress({
        ReferenceAddressStreet: watch('street') ?? '',
        ReferenceAddressHouseNumber: watch('houseNumber') ?? '',
        ReferenceAddressFloor: watch('floor') ?? '',
        ReferenceAddressDoor: watch('appartmentNumber') ?? '',
        ReferenceAddressZip: watch('postNumber') ?? '',
        ReferenceAddressCity: watch('city') ?? '',
      });
      setValue('fullAddress', addressFull);

      onAddAddress(getValues());
      toggleModal(modalId);
      setEditAddressKey(prevKey => prevKey + 1); // Increment key to force re-render of AutoCompleteInput
    }
  };

  return (
    <Box>
      <Stack direction="row" gap={1.25} justifyContent={'space-between'} alignItems={error ? 'center' : 'flex-end'}>
        <Box sx={{ flex: 3 }}>
          <InputField
            key={editAddressKey}
            control={control}
            label={'common.terms.address'}
            name="fullAddress"
            required
            slotProps={{
              htmlInput: {
                readOnly: true,
              },
            }}
          />
        </Box>
        <Button
          variant="outlined"
          size="medium"
          sx={{ width: 'fit-content', flex: 1 }}
          startIcon={<EditIcon />}
          onClick={() => toggleModal(modalId)}
        >
          {t('common.actions.editManually')}
        </Button>
      </Stack>
      {isModalOpen(modalId) && (
        <Modal
          modalTitle={t('modals.editAddress.title')}
          isOpen={isModalOpen(modalId)}
          toggleModal={() => toggleModal(modalId)}
          onConfirm={handleSubmit(handleSaveAddress)}
          confirmBtnLabel={t('common.actions.save')}
        >
          <Stack spacing={LAYOUT_SPACING.FORM.xs}>
            <InputField control={control} name="street" label="common.terms.street" required />
            <NumberInputField control={control} name="houseNumber" label="common.terms.houseNumber" required min={0} />
            <NumberInputField control={control} name="postNumber" label="common.terms.postNumber" required min={0} />
            <InputField control={control} name="city" label="common.terms.city" required />
            <NumberInputField control={control} name="floor" label="common.terms.floor" min={0} />
            <NumberInputField control={control} name="appartmentNumber" label="common.terms.appartmentNumber" min={0} />
          </Stack>
        </Modal>
      )}
    </Box>
  );
};

export default CreateOrderKeyPickUpAddress;
